import { useRef, useEffect, useCallback } from 'react';
import { useFlushOnPageHide } from '@/hooks/use-flush-on-page-hide';
import { useMutation } from '@apollo/client';
import { UPDATE_CLIP_DATA } from '@/queries/queries';

interface Interval {
  id: number;
  startTime: number;
  endTime: number;
  title?: string;
}

interface AutoSaveOptions {
  clipId: string | null;
  intervals: Interval[];
  sceneSequence?: boolean;
  /** The clip the CURRENT `intervals` belong to. During a clip switch there is
   *  a commit where `clipId` already points at the new clip while `intervals`
   *  still hold the old clip's data; without this tag the hook must guess
   *  which changes are loads and which are edits, and the guess ("skip the
   *  first change") swallowed the user's first edit whenever the load never
   *  appeared as a change — e.g. two clips whose segments serialize
   *  identically. */
  loadedClipId: string | null;
  enabled: boolean;
  delay?: number;
  onSaved?: (clipId: string, data: { segments: { start: number; end: number }[]; timelineSceneSequence?: boolean }) => void;
}

type PendingWrite = {
  clipId: string;
  segments: { start: number; end: number }[];
  serialized: string;
  sceneSequence?: boolean;
};

/** Debounced persistence of interval edits to clip.data.segments.
 *
 *  A pending (debounced) write is never dropped: switching clips fires the old
 *  clip's write immediately, and unmounting flushes whatever is pending. The
 *  first run whose intervals actually belong to `clipId` is the load echo — it
 *  seeds the diff baseline and is not saved. */
export function useAutoSaveClipData({
  clipId, intervals, loadedClipId, enabled, delay = 500, onSaved, sceneSequence,
}: AutoSaveOptions) {
  const [updateClipData, { loading: saving }] = useMutation(UPDATE_CLIP_DATA);
  const timeoutRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const pendingRef = useRef<PendingWrite | null>(null);
  const lastSavedRef = useRef<string>('');
  const awaitingLoadRef = useRef<boolean>(true);
  const currentClipIdRef = useRef<string | null>(null);
  const inFlightRef = useRef<Promise<void> | null>(null);
  const onSavedRef = useRef(onSaved);

  useEffect(() => {
    onSavedRef.current = onSaved;
  }, [onSaved]);

  const performSave = useCallback(
    async (write: PendingWrite) => {
      // Wait for any save already running so writes serialize.
      if (inFlightRef.current) {
        try { await inFlightRef.current; } catch { /* already logged */ }
      }
      const task = (async () => {
        try {
          const savedData = { segments: write.segments, ...(write.sceneSequence !== undefined ? { timelineSceneSequence: write.sceneSequence } : {}) };
          await updateClipData({
            variables: { id: write.clipId, data: savedData },
          });
          lastSavedRef.current = write.serialized;
          onSavedRef.current?.(write.clipId, savedData);
        } catch (error) {
          console.error('Auto-save clip data failed:', error);
        }
      })();
      inFlightRef.current = task.finally(() => {
        if (inFlightRef.current === task) {
          inFlightRef.current = null;
        }
      });
      await task;
    },
    [updateClipData],
  );

  /** Fire the pending write NOW (fire-and-forget). Never drops it. */
  const firePending = useCallback(() => {
    if (timeoutRef.current) {
      clearTimeout(timeoutRef.current);
      timeoutRef.current = null;
    }
    const pending = pendingRef.current;
    pendingRef.current = null;
    if (pending) void performSave(pending);
  }, [performSave]);
  // Tab close never unmounts: fire the pending write as a keepalive request.
  useFlushOnPageHide(firePending);
  const firePendingRef = useRef(firePending);
  useEffect(() => {
    firePendingRef.current = firePending;
  }, [firePending]);

  // Clip switch: the old clip's pending write must not die with the debounce.
  useEffect(() => {
    if (clipId !== currentClipIdRef.current) {
      firePending();
      currentClipIdRef.current = clipId;
      awaitingLoadRef.current = true;
      lastSavedRef.current = '';
    }
  }, [clipId, firePending]);

  // Debounced save.
  useEffect(() => {
    if (!enabled || !clipId) return;
    // Stale window: these intervals belong to another clip. Attribute nothing.
    if (loadedClipId !== clipId) return;

    const segments = intervals.map((i) => ({ start: Math.max(0, i.startTime), end: i.endTime }));
    const serialized = JSON.stringify({ segments, sceneSequence });

    // First run with THIS clip's own intervals — the load, not an edit.
    if (awaitingLoadRef.current) {
      awaitingLoadRef.current = false;
      lastSavedRef.current = serialized;
      return;
    }
    if (serialized === lastSavedRef.current) return;

    if (timeoutRef.current) clearTimeout(timeoutRef.current);
    pendingRef.current = { clipId, segments, serialized, sceneSequence };
    timeoutRef.current = setTimeout(() => {
      timeoutRef.current = null;
      const pending = pendingRef.current;
      pendingRef.current = null;
      if (pending) void performSave(pending);
    }, delay);
    // Cleanup clears only the TIMER; pendingRef survives so a re-run
    // reschedules it and the unmount flush below can still fire it.
    return () => {
      if (timeoutRef.current) clearTimeout(timeoutRef.current);
    };
  }, [intervals, sceneSequence, loadedClipId, clipId, enabled, delay, performSave]);

  // Unmount: flush instead of dropping. Navigating away within the debounce
  // window used to silently lose the last edit.
  useEffect(() => () => firePendingRef.current(), []);

  // Synchronous flush for callers that need the write on the wire before
  // continuing (e.g. right before a render/export call).
  const flush = useCallback(async () => {
    if (timeoutRef.current) {
      clearTimeout(timeoutRef.current);
      timeoutRef.current = null;
    }
    const pending = pendingRef.current;
    pendingRef.current = null;
    if (inFlightRef.current) {
      try { await inFlightRef.current; } catch { /* already logged */ }
    }
    if (pending) await performSave(pending);
  }, [performSave]);

  return { saving, flush };
}
